/**
 * 大文件上传（XHR 进度）+ 分片 + 重试续传 —— 参考《上传优化方案.md》
 * - ≤90MB：单请求 POST /api/files/upload（XHR 上报进度）
 * - >90MB：分片 POST /api/upload-chunk/{init,part,complete,abort}（32MB/片、并发 3、单片重试 5 次指数退避、可续传）
 */

const CHUNK_SIZE = 32 * 1024 * 1024
const SINGLE_LIMIT = 90 * 1024 * 1024

export interface UploadProgress {
  percent: number
  loaded: number
  total: number
  part?: number
  parts?: number
  /** bytes/s */
  speed?: number
  /** 秒 */
  eta?: number
}

export interface ResumeInfo {
  key: string
  uploadId: string
  chunkSize: number
  count: number
  size: number
  parts: { partNumber: number; etag: string }[]
}

function xhrUpload(url: string, form: FormData, onProgress?: (e: ProgressEvent) => void): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    if (onProgress && xhr.upload) xhr.upload.onprogress = onProgress
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)) } catch { resolve(xhr.responseText) }
      } else {
        let msg = '上传失败'
        try { msg = JSON.parse(xhr.responseText).error || msg } catch { /* ignore */ }
        const err: any = new Error(msg)
        err.status = xhr.status
        reject(err)
      }
    }
    xhr.onerror = () => reject(new Error('网络错误，请重试'))
    xhr.send(form)
  })
}

async function jsonApi(url: string, body: any) {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const d = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`)
  return d
}

/** 单文件通道（≤90MB）：XHR 直接传，带进度 */
async function uploadSingle(file: File, dir: string, onProgress: (p: UploadProgress) => void) {
  const form = new FormData()
  form.append('file', file)
  form.append('dir', dir)
  form.append('name', file.name)
  const start = performance.now()
  let lastLoaded = 0
  let lastT = start
  const data = await xhrUpload('/api/files/upload', form, (e) => {
    if (!e.lengthComputable) return
    const now = performance.now()
    const dt = (now - lastT) / 1000
    const delta = e.loaded - lastLoaded
    lastT = now
    lastLoaded = e.loaded
    const elapsed = (now - start) / 1000
    const speed = dt > 0 ? delta / dt : 0
    onProgress({
      percent: Math.round((e.loaded / e.total) * 100),
      loaded: e.loaded,
      total: e.total,
      speed,
      eta: e.loaded > 0 && elapsed > 0 ? (e.total - e.loaded) / (e.loaded / elapsed) : 0,
    })
  })
  return { key: data.key as string, url: (data.url as string) || `/api/files/${data.key}`, size: data.size || file.size }
}

/** 分片通道（>90MB） */
async function uploadChunked(file: File, dir: string, onProgress: (p: UploadProgress) => void, resume?: ResumeInfo | null) {
  let st: ResumeInfo
  if (resume && resume.size === file.size) {
    st = resume
  } else {
    const r = await jsonApi('/api/upload-chunk/init', { dir, name: file.name, size: file.size })
    st = { ...r.data, parts: [] }
  }
  const { key, uploadId, chunkSize, count } = st
  const done = new Map<number, string>((st.parts || []).map((p) => [p.partNumber, p.etag]))
  let loaded = [...done.keys()].reduce((sum, n) => {
    const startByte = (n - 1) * chunkSize
    return sum + Math.min(chunkSize, Math.max(0, file.size - startByte))
  }, 0)

  const start = performance.now()
  let lastLoaded = loaded
  let lastT = start
  const report = () => {
    const now = performance.now()
    const dt = (now - lastT) / 1000
    const delta = loaded - lastLoaded
    lastT = now
    lastLoaded = loaded
    const speed = dt > 0 ? delta / dt : 0
    onProgress({
      percent: Math.round((loaded / file.size) * 100),
      loaded,
      total: file.size,
      part: done.size,
      parts: count,
      speed,
      eta: speed > 0 ? (file.size - loaded) / speed : 0,
    })
  }
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

  const putPart = async (n: number) => {
    const blob = file.slice((n - 1) * chunkSize, Math.min(n * chunkSize, file.size))
    const form = new FormData()
    form.append('file', blob, `part-${n}`)
    form.append('key', key)
    form.append('uploadId', uploadId)
    form.append('partNumber', String(n))
    let lastErr: any = null
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const d = await xhrUpload('/api/upload-chunk/part', form)
        done.set(n, d.data.etag)
        loaded += blob.size
        report()
        return
      } catch (e) {
        lastErr = e
        if (attempt < 4) await sleep(Math.min(1000 * Math.pow(2, attempt), 8000))
      }
    }
    const resumeInfo: ResumeInfo = {
      key, uploadId, chunkSize, count, size: file.size,
      parts: [...done].map(([partNumber, etag]) => ({ partNumber, etag })),
    }
    const err: any = new Error(lastErr?.message || '分片多次失败')
    err.resumable = true
    err.resume = resumeInfo
    throw err
  }

  const missing = Array.from({ length: count }, (_, i) => i + 1).filter((n) => !done.has(n))
  let idx = 0
  const worker = async () => {
    for (;;) {
      const n = missing[idx++]
      if (n === undefined) return
      await putPart(n)
    }
  }
  try {
    await Promise.all([worker(), worker(), worker()])
  } catch (e: any) {
    if (e?.resumable) throw e
    throw e
  }

  await jsonApi('/api/upload-chunk/complete', {
    key,
    uploadId,
    parts: [...done].map(([partNumber, etag]) => ({ partNumber, etag })),
  })
  onProgress({ percent: 100, loaded: file.size, total: file.size, part: count, parts: count })
  return { key, url: `/api/files/${key}`, size: file.size }
}

export type UploadResult = { key: string; url: string; size: number }

/** 上传入口：按大小分流；resume 传入则续传 */
export async function uploadAny(
  file: File,
  dir: string,
  onProgress: (p: UploadProgress) => void,
  resume?: ResumeInfo | null,
): Promise<UploadResult> {
  if (file.size <= SINGLE_LIMIT) return uploadSingle(file, dir, onProgress)
  return uploadChunked(file, dir, onProgress, resume)
}

/** 取消分片上传（关闭弹窗时调用，服务端清理） */
export async function abortChunked(resume: ResumeInfo) {
  try {
    await jsonApi('/api/upload-chunk/abort', { key: resume.key, uploadId: resume.uploadId })
  } catch { /* ignore */ }
}
